import { useMemo, useState } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useConfirm } from '@/hooks/useConfirm';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useDomains } from '@/hooks/data/useDomains';
import { useNodes } from '@/hooks/data/useNodes';
import { useOriginGroups } from '@/hooks/data/useOriginGroups';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { PlusIcon } from '@/components/icons';
import {
  ClearFiltersButton,
  ErrorAlert,
  PageHeader,
  SearchField,
} from '@/components/PageParts';
import { DomainModal } from '@/components/DomainModal';
import { DomainsTable, type DomainRowAction } from '@/components/domains/DomainsTable';
import {
  DnsValidationModal,
  type DnsValidationOutcome,
} from '@/components/domains/DnsValidationModal';
import { domainStatusMap } from '@/config/statusMaps';
import { getDomainActions } from '@/config/resourceRules';
import { pluralize } from '@/utils/format';
import type { Domain, DomainStatus, UpdateDomainRequest } from '@/types/domain';
import './Domains.css';

/**
 * Delay before a typed search query is sent to the server
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Domains page component
 *
 * Features:
 * - List all domains with filtering (server-side, search debounced)
 * - Add, edit and delete domains (operator+)
 * - DNS validation (operator+)
 */
export function Domains() {
  const { role, can } = usePermissions();

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<DomainStatus | ''>('');
  const [nodeFilter, setNodeFilter] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery.trim(), SEARCH_DEBOUNCE_MS);

  const {
    domains,
    isLoading,
    error: loadError,
    reload,
    createDomain,
    updateDomain,
    deleteDomain,
    validateDns,
  } = useDomains({
    node_id: nodeFilter || undefined,
    status: statusFilter || undefined,
    search: debouncedSearch || undefined,
  });

  // Options for filters and the modal (failures are non-critical)
  const { nodes } = useNodes();
  const { originGroups } = useOriginGroups();

  // UI state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [validationOutcome, setValidationOutcome] = useState<DnsValidationOutcome | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();

  const validateAction = useAsyncAction((domain: Domain) => validateDns(domain.id), {
    errorMessage: 'DNS validation failed',
    getKey: (domain) => domain.id,
  });

  const nodeNames = useMemo(() => new Map(nodes.map((n) => [n.id, n.name])), [nodes]);

  /**
   * Names of the (known) nodes serving a domain
   */
  const getNodeNames = (nodeIds: string[]): string => {
    const names = (nodeIds ?? [])
      .map((id) => nodeNames.get(id))
      .filter((name): name is string => !!name);
    return names.length > 0 ? names.join(', ') : '-';
  };

  const openModal = (domain: Domain | null) => {
    setSelectedDomain(domain);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDomain(null);
  };

  /**
   * Update and keep the modal's baseline in sync with the saved domain
   */
  const handleUpdate = async (id: string, data: UpdateDomainRequest) => {
    const updated = await updateDomain(id, data);
    setSelectedDomain(updated);
    return updated;
  };

  /**
   * Validate DNS; failures are shown in the same result dialog
   */
  const handleValidateDns = async (domain: Domain) => {
    const outcome = await validateAction.run(domain);
    setValidationOutcome({
      domainName: domain.name,
      result: outcome.ok
        ? outcome.value
        : { validated: false, dns_records: [], expected_ip: '', error: outcome.error },
    });
  };

  const handleRowAction = (action: DomainRowAction, domain: Domain) => {
    setActionError(null);
    switch (action) {
      case 'validateDns':
        void handleValidateDns(domain);
        return;
      case 'edit':
        openModal(domain);
        return;
      case 'delete':
        void confirm({
          title: 'Delete Domain',
          message: (
            <>
              Are you sure you want to delete <strong>{domain.name}</strong>? This action cannot be
              undone and will remove all associated configurations and certificates.
            </>
          ),
          confirmLabel: 'Delete',
          onConfirm: () => deleteDomain(domain.id),
          errorMessage: 'Failed to delete domain',
        });
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setNodeFilter('');
  };

  const handleRetry = () => {
    setActionError(null);
    void reload();
  };

  const hasActiveFilters = Boolean(searchQuery || statusFilter || nodeFilter);
  const canCreate = can('domains:create');

  return (
    <div className="domains-page">
      <PageHeader
        classPrefix="domains"
        title="Domains"
        count={pluralize(domains.length, 'domain')}
        actions={
          canCreate && (
            <Button leftIcon={<PlusIcon />} onClick={() => openModal(null)}>
              Add Domain
            </Button>
          )
        }
      />

      {/* Filters */}
      <div className="domains-filters">
        <SearchField
          classPrefix="domains"
          placeholder="Search domains..."
          value={searchQuery}
          onChange={setSearchQuery}
        />

        <Select
          value={nodeFilter}
          onChange={setNodeFilter}
          placeholder="All Nodes"
          options={nodes.map((node) => ({ value: node.id, label: node.name }))}
          className="domains-filter-select"
          aria-label="Filter by node"
        />

        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          placeholder="All Status"
          options={domainStatusMap.options}
          className="domains-filter-select"
          aria-label="Filter by status"
        />

        {hasActiveFilters && <ClearFiltersButton classPrefix="domains" onClick={handleClearFilters} />}
      </div>

      <ErrorAlert classPrefix="domains" message={actionError ?? loadError} onRetry={handleRetry} />

      <DomainsTable
        domains={domains}
        isLoading={isLoading}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        onAddFirst={canCreate ? () => openModal(null) : undefined}
        getNodeNames={getNodeNames}
        getActions={(domain) => getDomainActions(role, domain)}
        isBusy={(_action, domainId) => validateAction.isPending(domainId)}
        onAction={handleRowAction}
      />

      <DomainModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onCreate={createDomain}
        onUpdate={handleUpdate}
        domain={selectedDomain}
        nodes={nodes}
        originGroups={originGroups}
      />

      <DnsValidationModal outcome={validationOutcome} onClose={() => setValidationOutcome(null)} />

      {confirmDialog}
    </div>
  );
}
